import { useCallback, useEffect, useRef, useState } from "react";
import SparkMD5 from "spark-md5";
import { api } from "../api";
import { directUpload } from "./directUpload";
import { rememberFocus } from "../focus";
import { formatBytes } from "./Feedback";
import { Btn, G_BG, ProgressBar } from "./Shared";

// Upload files into a dataset: pick or drop, then each file is sent as a raw
// body to POST /api/datasets/{id}/files?filename=... with its MD5 computed here
// (X-Content-MD5), so the server checks the bytes against something it did not
// write itself (lesson from data-portal: a checksum written by the same pass that
// wrote the data proves nothing).
//
// Refused before sending: 0-byte entries and folders (a dropped folder arrives
// as a File of size 0 — data-portal's "empty uploads" incident).
//
// A name that already exists in the dataset (409) is offered for replacement: after a
// confirmation the same file is sent again with ?replace=true. The server replaces it in
// place, keeping the dictionary entries, key links and DB-design decisions of columns
// whose name is unchanged.
//
// A file over the portal's size limit is refused with 413, too many uploads at once with 429
// (MAX_UPLOAD_BYTES, MAX_CONCURRENT_UPLOADS_PER_USER). The server's own words are shown;
// `uploadErrorText` only fills in when a proxy answered without them.
//
// Large files go straight to S3 (directUpload.js) from `directMinBytes` (the parent reads it
// from GET /api/uploads/config, set in Management -> Storage; 0 = off): only for dataset files,
// not for another `path`. Such an upload survives a dropped network or a reload: its parts stay in S3, the
// panel lists the unfinished ones (with "Discard"), and choosing the same file again — or
// "Resume" on a failed row — sends only what is missing.
//
// Props: datasetId, onUploaded(out) called after each successful file with the server's answer.
// The same panel can upload to another target (it did to protocol attachments until 2026-10-20):
//   path         the upload URL relative to /api (default: /datasets/{datasetId}/files)
//   label        the section's accessible name (default "Upload files")
//   hint         the text in the drop zone
//   replaceable  false: a 409 is shown as the server's refusal, with no "Replace" offer
//                (for a target with no replace-in-place)
//   directMinBytes  files of at least this size upload straight to S3 (default 0 = never)

const CHUNK = 8 * 1024 * 1024;

export async function md5OfFile(file) {
  const spark = new SparkMD5.ArrayBuffer();
  for (let offset = 0; offset < file.size; offset += CHUNK) {
    spark.append(await file.slice(offset, offset + CHUNK).arrayBuffer());
  }
  return spark.end();
}

export function uploadErrorText(err) {
  if (typeof err.detail === "string" && err.detail) return err.message;
  if (err.status === 413) return "The file is larger than this portal accepts. Nothing was stored.";
  if (err.status === 429) return "Too many uploads at once. Wait for one to finish, then try again.";
  return err.message;
}

export function sortSelection(fileList, items) {
  const ok = [];
  const refused = [];
  Array.from(fileList || []).forEach((f, i) => {
    const entry = items && items[i] && items[i].webkitGetAsEntry ? items[i].webkitGetAsEntry() : null;
    if ((entry && entry.isDirectory) || f.size === 0) refused.push(f.name);
    else ok.push(f);
  });
  return { ok, refused };
}

const DATASET_HINT = "Drop files here, or choose them. CSV, TSV and XLSX files get a column dictionary.";

export default function UploadPanel({ datasetId, onUploaded, path, label = "Upload files", hint = DATASET_HINT,
                                      replaceable = true, directMinBytes = 0 }) {
  const target = path || `/datasets/${datasetId}/files`;
  const inputRef = useRef(null);
  const chooseRef = useRef(null);            // wraps "Choose files": focus returns there after an upload
  const chooseButton = () => chooseRef.current && chooseRef.current.querySelector("button");
  const [queue, setQueue] = useState([]);   // [{name, size, progress, status, error}]
  const [refused, setRefused] = useState([]);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const filesRef = useRef([]);               // the File of each queue entry, for "Replace"
  const [unfinished, setUnfinished] = useState([]);
  const [discardError, setDiscardError] = useState(null);
  const canDirect = !path && !!datasetId;
  const threshold = canDirect ? directMinBytes : 0;     // direct-to-S3 from this many bytes; 0 = off

  const refreshUnfinished = useCallback(() => {
    if (!canDirect || !threshold) return Promise.resolve();
    return api.get(`/datasets/${datasetId}/direct-uploads`)
      .then(list => setUnfinished(list || []))
      .catch(() => { /* a reader without write access: nothing to resume */ });
  }, [canDirect, threshold, datasetId]);

  useEffect(() => { refreshUnfinished(); }, [refreshUnfinished]);

  const update = (i, patch) => setQueue(q => q.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const send = async (i, f, replace) => {
    const direct = canDirect && threshold > 0 && f.size >= threshold;
    try {
      update(i, { status: "checksum", error: null, progress: 0, direct });
      let out;
      if (direct) {
        out = await directUpload({
          datasetId, file: f, replace,
          onProgress: p => update(i, { progress: p * 100 }),
          onStatus: st => update(i, { status: st }),
        });
      } else {
        const md5 = await md5OfFile(f);
        update(i, { status: "uploading" });
        const query = replace ? "?replace=true" : "";
        out = await api.upload(`${target}${query}`, f, {
          md5, onProgress: p => update(i, { progress: p * 100 }),
        });
      }
      update(i, { status: replace ? "replaced" : "done", progress: 100 });
      if (onUploaded) onUploaded(out);
    } catch (err) {
      const nameTaken = err.status === 409 && (!direct || /already has a file/.test(err.message || ""));
      if (nameTaken && !replace && replaceable) update(i, { status: "exists", error: null });
      else update(i, { status: "failed", error: uploadErrorText(err), resumeReplace: replace });
    }
    if (direct) await refreshUnfinished();
  };

  const resume = async i => {
    const restore = rememberFocus();
    setBusy(true);
    await send(i, filesRef.current[i], !!queue[i].resumeReplace);
    setBusy(false);
    restore(chooseButton);
  };

  const discard = async u => {
    setDiscardError(null);
    try {
      await api.del(`/direct-uploads/${u.id}`);
    } catch (err) {
      setDiscardError(err.message);
    }
    await refreshUnfinished();
  };

  const start = async files => {
    if (files.length === 0) return;
    const restore = rememberFocus();         // "Choose files" is disabled while busy and drops focus
    setBusy(true);
    const base = queue.length;
    setQueue(q => [...q, ...files.map(f => ({ name: f.name, size: f.size, progress: 0, status: "queued" }))]);
    for (let k = 0; k < files.length; k += 1) {
      filesRef.current[base + k] = files[k];
      await send(base + k, files[k], false);
    }
    setBusy(false);
    restore(chooseButton);
  };

  const replace = async i => {
    const restore = rememberFocus();
    setBusy(true);
    await send(i, filesRef.current[i], true);
    setBusy(false);
    restore(chooseButton);
  };

  const statusLabel = status => ({ failed: "failed", done: "uploaded", replaced: "replaced",
                             exists: "already exists", verifying: "checking the stored file" }[status] || status);
  const inQueue = new Set(queue.filter(q => !["done", "replaced", "failed", "skipped"].includes(q.status))
    .map(q => q.name));
  const waiting = unfinished.filter(u => !inQueue.has(u.filename) && !u.completing);

  const pick = e => {
    const { ok, refused: r } = sortSelection(e.target.files, null);
    setRefused(r);
    start(ok);
    e.target.value = "";
  };

  const drop = e => {
    e.preventDefault();
    setOver(false);
    const { ok, refused: r } = sortSelection(e.dataTransfer.files, e.dataTransfer.items);
    setRefused(r);
    start(ok);
  };

  return (
    <section aria-label={label} style={{ marginBottom: 20 }}>
      <div
        onDragOver={e => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={drop}
        style={{
          border: `2px dashed ${over ? "var(--dp-accent)" : "#d1d5db"}`, borderRadius: 12,
          background: over ? "var(--dp-accent-bg)" : "#fafafa", padding: "22px 20px", textAlign: "center",
        }}
      >
        <p style={{ margin: "0 0 10px", fontSize: 14, color: "#374151" }}>
          {hint}
        </p>
        <input ref={inputRef} type="file" multiple onChange={pick} aria-label="Choose files to upload" style={{ display: "none" }} />
        <span ref={chooseRef}>
          <Btn variant="ghost" onClick={() => inputRef.current && inputRef.current.click()} disabled={busy}>Choose files</Btn>
        </span>
      </div>
      {refused.length > 0 && (
        <div role="alert" style={{ marginTop: 10, color: "#92400e", fontSize: 13 }}>
          Not uploaded (empty, or a folder): {refused.join(", ")}. Upload the files inside a folder instead.
        </div>
      )}
      {waiting.length > 0 && (
        <div role="group" aria-label="Unfinished uploads"
             style={{ marginTop: 10, padding: "10px 12px", borderRadius: 10, background: "#fafafa",
                      border: "1px solid #e5e7eb", fontSize: 13, color: "#374151" }}>
          <div style={{ marginBottom: 6 }}>
            Unfinished uploads straight to storage. Choose the same file again to resume, or discard it.
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
            {waiting.map(u => (
              <li key={u.id} role="group" aria-label={`Unfinished upload of ${u.filename}`}
                  style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ flex: 1 }}>{u.filename} <span style={{ color: "#6b7280" }}>({formatBytes(u.size)})</span></span>
                <Btn variant="secondary" onClick={() => discard(u)} disabled={busy}>Discard</Btn>
              </li>
            ))}
          </ul>
          {discardError && <div role="alert" style={{ marginTop: 6, color: "#b91c1c" }}>{discardError}</div>}
        </div>
      )}
      {queue.length > 0 && (
        <ul aria-live="polite" style={{ listStyle: "none", padding: 0, margin: "12px 0 0", display: "grid", gap: 8 }}>
          {queue.map((q, i) => (
            <li key={i} style={{ fontSize: 13, color: "#374151" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                <span>{q.name} <span style={{ color: "#6b7280" }}>({formatBytes(q.size)})</span></span>
                <span>{statusLabel(q.status)}</span>
              </div>
              {q.status === "exists" ? (
                <div role="group" aria-label={`Replace ${q.name}?`}
                     style={{ marginTop: 6, padding: "10px 12px", borderRadius: 10, background: G_BG,
                              border: "1px solid rgba(0,150,64,0.3)", display: "flex", gap: 10,
                              alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ flex: 1, minWidth: 220 }}>
                    This dataset already has a file named <strong>{q.name}</strong>. Replace it with this one?
                    Titles, descriptions, rules, key links and DB-design decisions of columns with the same
                    name are kept.
                  </span>
                  <Btn onClick={() => replace(i)} disabled={busy}>Replace</Btn>
                  <Btn variant="secondary" onClick={() => { update(i, { status: "skipped" }); rememberFocus()(chooseButton); }} disabled={busy}>
                    Keep the existing file
                  </Btn>
                </div>
              ) : q.status !== "failed" && q.status !== "skipped" && (
                <ProgressBar value={q.progress} label={`Upload of ${q.name}`} />
              )}
              {q.error && <div role="alert" style={{ color: "#b91c1c" }}>{q.error}</div>}
              {q.status === "failed" && q.direct && filesRef.current[i] && (
                <div style={{ marginTop: 6 }}>
                  <Btn variant="secondary" onClick={() => resume(i)} disabled={busy}>Resume</Btn>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
